import React, { useState } from 'react';
import { validateEmail } from '../../lib/validation';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import AddressAutocomplete from '../AddressAutocomplete';

interface AddContactDialogProps {
  onClose: () => void;
  onAdd: (type: 'address' | 'email' | 'phone', data: any) => Promise<void>;
}

export const AddContactDialog: React.FC<AddContactDialogProps> = ({ onClose, onAdd }) => {
  const [contactType, setContactType] = useState<'address' | 'email' | 'phone'>('address');
  const [error, setError] = useState<string | null>(null);
  const [addressData, setAddressData] = useState<{
    address: string;
    city: string;
    state: string;
    zip_code: string;
  } | null>(null);

  const formatPhoneNumber = (input: string) => {
    const cleaned = input.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return cleaned;
  };

  return (
    <Dialog open onOpenChange={() => {
      onClose();
      setError(null);
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Contact Information</DialogTitle>
          <DialogDescription>
            Add new contact details
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
            {error}
          </div>
        )}

        <Tabs defaultValue="address" onValueChange={(value) => setContactType(value as any)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="address">Address</TabsTrigger>
            <TabsTrigger value="phone">Phone</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
          </TabsList>

          <form onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);
            
            try {
              if (contactType === 'address') {
                if (!addressData) {
                  setError('Please select a valid address from the suggestions');
                  return;
                }

                await onAdd('address', {
                  ...addressData,
                  is_primary: formData.get('is_primary') === 'on',
                  label: formData.get('label')
                });
              } else if (contactType === 'phone') {
                const phoneInput = formData.get('phone') as string;
                const formattedPhone = formatPhoneNumber(phoneInput);
                
                if (formattedPhone.length !== 14) {
                  setError('Please enter a valid 10-digit phone number');
                  return;
                }

                await onAdd('phone', {
                  phone: formattedPhone,
                  is_primary: formData.get('is_primary') === 'on',
                  label: formData.get('label')
                });
              } else if (contactType === 'email') {
                const email = formData.get('email') as string;
                if (!validateEmail(email)) {
                  setError('Please enter a valid email address');
                  return;
                }

                await onAdd('email', {
                  email: email,
                  is_primary: formData.get('is_primary') === 'on',
                  label: formData.get('label')
                });
              }
              onClose();
            } catch (error) {
              console.error('Failed to add contact:', error);
              setError(error instanceof Error ? error.message : 'Failed to add contact');
            }
          }} className="space-y-4 mt-4">
            <TabsContent value="address">
              <div className="space-y-4">
                <AddressAutocomplete
                  onAddressSelect={(address) => {
                    setAddressData(address);
                  }}
                  className="w-full"
                />
              </div>
            </TabsContent>

            <TabsContent value="phone">
              <Input 
                type="tel" 
                name="phone" 
                placeholder="(555) 123-4567"
                required
                onChange={(e) => {
                  e.target.value = formatPhoneNumber(e.target.value);
                }}
              />
            </TabsContent>

            <TabsContent value="email">
              <Input 
                type="email" 
                name="email" 
                placeholder="email@example.com"
                required
              />
            </TabsContent>

            <div className="space-y-4">
              <div>
                <Label>Label (Optional)</Label>
                <Input 
                  type="text" 
                  name="label" 
                  placeholder="e.g., Home, Work, etc."
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  name="is_primary"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="is_primary">Set as primary</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="submit">Add Contact</Button>
            </DialogFooter>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};